import { useEffect, useState, useCallback, useMemo } from "react";
import { TrendingUp, Euro, CalendarDays, X, Users, Download, FileSpreadsheet, FileText, ArrowUp, ArrowDown, ChevronsUpDown, Filter, GripVertical } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { fmtDate } from "@/lib/time";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const pad2 = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
const weekDays = () => { const s = startOfWeek(new Date()); return Array.from({ length: 7 }, (_, i) => { const d = new Date(s); d.setDate(s.getDate() + i); return d; }); };
const monthDays = () => { const now = new Date(); const y = now.getFullYear(), m = now.getMonth(); const n = new Date(y, m + 1, 0).getDate(); return Array.from({ length: n }, (_, i) => new Date(y, m, i + 1)); };

const COLS = [
  { id: "tech_name", label: "Tecnico", kind: "text", w: 220, align: "left" },
  { id: "pieces", label: "Pezzi", kind: "num", w: 100, align: "right" },
  { id: "revenue", label: "Ricavo", kind: "eur", w: 150, align: "right" },
  { id: "hours", label: "Ore (presenze)", kind: "hours", w: 140, align: "right" },
  { id: "labor_cost", label: "Costo manodopera", kind: "eur", w: 160, align: "right" },
  { id: "fuel_cost", label: "Carburante", kind: "eur", w: 140, align: "right" },
  { id: "margin", label: "Margine", kind: "eur", w: 150, align: "right" },
  { id: "per_hour", label: "€ / ora", kind: "eur", w: 120, align: "right" },
];
const STORE = "flux_risultati_cols_v1";
const fmtCell = (r, c) => {
  const v = r[c.id];
  if (c.kind === "eur") return eur(v);
  if (c.kind === "hours") return `${v} h`;
  return v ?? "";
};

export default function Risultati() {
  const [days, setDays] = useState([new Date()]);
  const [data, setData] = useState(null);
  const [sort, setSort] = useState({ col: "margin", dir: "desc" });
  const [colFilters, setColFilters] = useState({});
  const [dragCol, setDragCol] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [colState, setColState] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(STORE));
      if (s?.order) {
        const missing = COLS.map((c) => c.id).filter((id) => !s.order.includes(id));
        return { order: [...s.order, ...missing], widths: s.widths || {} };
      }
    } catch { /* ignore */ }
    return { order: COLS.map((c) => c.id), widths: {} };
  });
  useEffect(() => { localStorage.setItem(STORE, JSON.stringify(colState)); }, [colState]);

  const load = useCallback(() => {
    const dates = days.map(toISO);
    api.get("/risultati", { params: { dates: JSON.stringify(dates) } })
      .then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const rawRows = data?.rows || [];
  const label = days.length === 0 ? "Nessun giorno" : days.length === 1 ? fmtDate(toISO(days[0])) : `${days.length} giorni`;

  const cols = colState.order.map((id) => COLS.find((c) => c.id === id)).filter(Boolean);
  const colW = (c) => colState.widths?.[c.id] ?? c.w;

  const rows = useMemo(() => {
    let out = rawRows.filter((r) => cols.every((c) => {
      const fv = colFilters[c.id];
      if (c.id === "tech_name" && Array.isArray(fv)) {
        return fv.length === 0 || fv.includes(r.tech_name);
      }
      const f = (fv || "").trim().toLowerCase();
      if (!f) return true;
      return String(fmtCell(r, c)).toLowerCase().includes(f);
    }));
    const c = COLS.find((x) => x.id === sort.col);
    if (c) {
      out = [...out].sort((a, b) => {
        const av = a[c.id], bv = b[c.id];
        const cmp = c.kind === "text" ? String(av || "").localeCompare(String(bv || "")) : (Number(av || 0) - Number(bv || 0));
        return sort.dir === "asc" ? cmp : -cmp;
      });
    }
    return out;
  }, [rawRows, colFilters, sort, colState.order]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => ({
    pieces: rows.reduce((s, r) => s + (r.pieces || 0), 0),
    revenue: rows.reduce((s, r) => s + (r.revenue || 0), 0),
    hours: rows.reduce((s, r) => s + (r.hours || 0), 0),
    labor_cost: rows.reduce((s, r) => s + (r.labor_cost || 0), 0),
    fuel_cost: rows.reduce((s, r) => s + (r.fuel_cost || 0), 0),
    cost: rows.reduce((s, r) => s + (r.cost || 0), 0),
    margin: rows.reduce((s, r) => s + (r.margin || 0), 0),
  }), [rows]);

  const toggleSort = (id) => setSort((s) => (s.col === id ? { col: id, dir: s.dir === "asc" ? "desc" : "asc" } : { col: id, dir: id === "tech_name" ? "asc" : "desc" }));
  const filterActive = (id) => (id === "tech_name" && Array.isArray(colFilters[id])) ? colFilters[id].length > 0 : !!(colFilters[id] || "").trim();
  const techNames = useMemo(() => [...new Set(rawRows.map((r) => r.tech_name))].sort((a, b) => String(a).localeCompare(String(b))), [rawRows]);
  const toggleTech = (name) => setColFilters((f) => {
    const cur = Array.isArray(f.tech_name) ? f.tech_name : [];
    return { ...f, tech_name: cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name] };
  });

  const onDrop = (targetId) => {
    if (!dragCol || dragCol === targetId) return;
    setColState((s) => {
      const order = [...s.order];
      order.splice(order.indexOf(targetId), 0, order.splice(order.indexOf(dragCol), 1)[0]);
      return { ...s, order };
    });
    setDragCol(null);
  };
  const startResize = (e, id) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startW = colState.widths?.[id] ?? (COLS.find((c) => c.id === id)?.w || 120);
    const onMove = (ev) => setColState((s) => ({ ...s, widths: { ...s.widths, [id]: Math.max(70, startW + (ev.clientX - startX)) } }));
    const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const doExport = async (format) => {
    if (days.length === 0) { toast.error("Seleziona almeno un giorno"); return; }
    setExporting(true);
    try {
      const dates = days.map(toISO);
      const r = await api.get("/risultati/export", { params: { dates: JSON.stringify(dates), format }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `risultati_${dates[0]}.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
    finally { setExporting(false); }
  };

  const totalWidth = cols.reduce((s, c) => s + colW(c), 0);
  const cellCls = (c) => cn("shrink-0 overflow-hidden px-4 py-2.5 text-sm", c.align === "right" ? "text-right" : "text-left");

  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader overline="Flux · Analisi" title="Risultati" subtitle="Resa per periodo: pezzi completati, ricavo, costo (da presenze) e margine per tecnico">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" data-testid="ris-date">
              <CalendarDays size={16} />{label}
              {days.length > 0 && <span onClick={(e) => { e.stopPropagation(); setDays([]); }} data-testid="ris-date-clear" className="ml-1 rounded-full p-0.5 hover:bg-secondary"><X size={13} /></span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <div className="flex flex-wrap gap-1.5 border-b border-border p-2">
              <Button variant="outline" size="sm" onClick={() => setDays([new Date()])} data-testid="ris-today">Oggi</Button>
              <Button variant="outline" size="sm" onClick={() => setDays(weekDays())} data-testid="ris-preset-week">Questa settimana</Button>
              <Button variant="outline" size="sm" onClick={() => setDays(monthDays())} data-testid="ris-preset-month">Questo mese</Button>
              <Button variant="ghost" size="sm" onClick={() => setDays([])} data-testid="ris-preset-custom">Personalizzato</Button>
            </div>
            <Calendar mode="multiple" selected={days} onSelect={(d) => setDays(d || [])} />
            <div className="border-t border-border p-2 text-center text-xs text-muted-foreground">{days.length === 1 ? "1 giorno selezionato" : `${days.length} giorni selezionati`}</div>
          </PopoverContent>
        </Popover>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button disabled={exporting} data-testid="ris-export"><Download size={16} />{exporting ? "Esporto…" : "Esporta"}</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => doExport("xlsx")} data-testid="ris-export-xlsx"><FileSpreadsheet size={15} className="mr-2" />Excel (.xlsx)</DropdownMenuItem>
            <DropdownMenuItem onClick={() => doExport("pdf")} data-testid="ris-export-pdf"><FileText size={15} className="mr-2" />PDF</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </PageHeader>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded-xl border border-border bg-card p-4" data-testid="ris-tot-pieces">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground"><TrendingUp size={14} />Pezzi completati</div>
          <p className="mt-1 text-3xl font-extrabold text-navy">{totals.pieces}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4" data-testid="ris-tot-revenue">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground"><Euro size={14} />Ricavo</div>
          <p className="mt-1 text-3xl font-extrabold text-emerald-600">{eur(totals.revenue)}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4" data-testid="ris-tot-cost">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground"><Euro size={14} />Costo</div>
          <p className="mt-1 text-3xl font-extrabold text-navy">{eur(totals.cost)}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4" data-testid="ris-tot-margin">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground"><TrendingUp size={14} />Margine</div>
          <p className={cn("mt-1 text-3xl font-extrabold", totals.margin >= 0 ? "text-emerald-600" : "text-destructive")}>{eur(totals.margin)}</p>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <div style={{ minWidth: totalWidth }}>
          <div className="flex border-b border-border bg-secondary">
            {cols.map((c) => (
              <div key={c.id} draggable onDragStart={() => setDragCol(c.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(c.id)}
                style={{ width: colW(c) }}
                className={cn("group relative flex shrink-0 items-center gap-1 whitespace-nowrap px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground", c.align === "right" && "justify-end")}
                data-testid={`ris-col-${c.id}`}>
                <GripVertical size={12} className="cursor-move opacity-0 group-hover:opacity-50" />
                <button onClick={() => toggleSort(c.id)} className="flex items-center gap-1 hover:text-navy" data-testid={`ris-sort-${c.id}`}>
                  {c.label}
                  {sort.col === c.id ? (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ChevronsUpDown size={11} className="opacity-30" />}
                </button>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className={cn("rounded p-0.5 hover:text-navy", filterActive(c.id) ? "text-primary" : "text-muted-foreground/50")} data-testid={`ris-filter-btn-${c.id}`}>
                      <Filter size={12} className={filterActive(c.id) ? "fill-primary" : ""} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-56 p-2">
                    {c.id === "tech_name" ? (
                      <div>
                        <div className="mb-2 flex items-center justify-between gap-2 border-b border-border pb-2">
                          <button onClick={() => setColFilters((f) => ({ ...f, tech_name: techNames.slice() }))} className="text-xs font-semibold text-primary hover:underline" data-testid="ris-tech-select-all">Tutti</button>
                          <button onClick={() => setColFilters((f) => ({ ...f, tech_name: [] }))} className="text-xs font-semibold text-muted-foreground hover:text-navy" data-testid="ris-tech-clear">Azzera</button>
                        </div>
                        <div className="max-h-64 space-y-1 overflow-y-auto">
                          {techNames.map((name) => (
                            <label key={name} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-secondary">
                              <Checkbox checked={Array.isArray(colFilters.tech_name) && colFilters.tech_name.includes(name)} onCheckedChange={() => toggleTech(name)} data-testid={`ris-filter-tech-${name}`} />
                              {name}
                            </label>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Input autoFocus placeholder={`Filtra ${c.label}`} value={colFilters[c.id] || ""} onChange={(e) => setColFilters((f) => ({ ...f, [c.id]: e.target.value }))} data-testid={`ris-filter-${c.id}`} />
                    )}
                  </PopoverContent>
                </Popover>
                <div onMouseDown={(e) => startResize(e, c.id)} onClick={(e) => e.stopPropagation()} draggable={false} onDragStart={(e) => e.preventDefault()} title="Trascina per ridimensionare" data-testid={`ris-resize-${c.id}`} className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize bg-transparent hover:bg-primary/40" />
              </div>
            ))}
          </div>

          {rows.length === 0 ? (
            <div className="px-4 py-10 text-center text-muted-foreground">Nessun tecnico</div>
          ) : rows.map((r) => (
            <div key={r.tech_id} className="flex border-b border-border/60 hover:bg-secondary/40" data-testid={`ris-row-${r.tech_name}`}>
              {cols.map((c) => (
                <div key={c.id} style={{ width: colW(c) }} className={cn(cellCls(c),
                  c.id === "tech_name" && "font-semibold text-navy",
                  c.id === "pieces" && "font-bold text-navy",
                  c.id === "revenue" && "text-emerald-600",
                  c.id === "per_hour" && "font-semibold text-navy",
                  c.id === "margin" && cn("font-bold", r.margin >= 0 ? "text-emerald-600" : "text-destructive"),
                )}>
                  {c.id === "tech_name" ? (
                    <span className="flex items-center gap-1.5">
                      {r.tech_name}
                      {r.team_days > 0 && (
                        <span className="inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary" title="Giorni lavorati in coppia (costo = somma delle tariffe)" data-testid={`ris-teamdays-${r.tech_name}`}>
                          <Users size={11} />{r.team_days} in coppia
                        </span>
                      )}
                    </span>
                  ) : fmtCell(r, c)}
                </div>
              ))}
            </div>
          ))}

          {rows.length > 0 && (
            <div className="flex border-t-2 border-border bg-secondary/40 font-bold text-navy">
              {cols.map((c) => (
                <div key={c.id} style={{ width: colW(c) }} className={cn(cellCls(c), "py-3", c.id === "margin" && (totals.margin >= 0 ? "text-emerald-600" : "text-destructive"), c.id === "revenue" && "text-emerald-600")}>
                  {c.id === "tech_name" ? "Totale"
                    : c.id === "pieces" ? totals.pieces
                    : c.id === "hours" ? `${Number(totals.hours.toFixed(2))} h`
                    : c.id === "per_hour" ? ""
                    : eur(totals[c.id])}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground"><Users size={12} className="mr-1 inline" />Le ore e il compagno di squadra si registrano nel menu <b>Presenze</b>. Nei giorni feriali le prime 8 ore sono a tariffa ordinaria, le ore oltre le 8 a tariffa <b>straordinaria</b>; sabato e domenica sono sempre a tariffa straordinaria. Il costo del <b>carburante</b> del tecnico confluisce nel costo della giornata: prima con l'importo della richiesta, poi con l'importo effettivo quando viene caricato lo scontrino. I ticket di categoria <b>Assurance</b> generano ricavo solo se il picco è <b>Visibile</b> (altrimenti ricavo nullo). Il badge <b>"in coppia"</b> indica i giorni in squadra doppia: il costo è la somma delle tariffe orarie dei due tecnici. Clicca le intestazioni per ordinare, l'imbuto per filtrare; trascina per spostare o ridimensionare le colonne.</p>
    </div>
  );
}
